import {
    formatCustomValue,
    formatMinor,
    getBillToCustomValue,
    getCustomValue,
    invoiceNotes,
    visibleBillToFields,
    visibleColumns,
} from '../helpers';
import {
    Invoice,
    InvoiceBuilderColumn,
    InvoiceBuilderCustomColumn,
    InvoiceLine,
} from '../types';

export const PRESTIGE_INVOICE_AGENT_IDS = [
    'TqPzkkMHGAhYjWrJ9neo',
    'WbrA7WhIweqRXl6WK3aJ',
] as const;

export const PRESTIGE_BODY_ROWS = 18;

export const isPrestigeInvoiceAgent = (agentId?: string | null) => (
    typeof agentId === 'string'
    && (PRESTIGE_INVOICE_AGENT_IDS as readonly string[]).includes(agentId)
);

const normalizeLabel = (label: string) => (
    label
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
);

const REMITENTE_NOMBRE = ['NOMBREDELREMITENTE', 'NOMBRE', 'NAME'];
const REMITENTE_TELEFONO = ['TELEFONODELREMITENTE', 'TELEFONO', 'TEL', 'PHONE'];
const REMITENTE_CI = ['CIDELREMITENTE', 'CI'];
const DESTINATARIO_NOMBRE = ['NOMBREDELDESTINATARIO'];
const DESTINATARIO_TELEFONO = ['TELEFONODELDESTINATARIO'];
const DESTINATARIO_CI = ['CIDELDESTINATARIO'];
const AUTO_KEYS = ['AUTOMARCAYMODELO', 'AUTO', 'VEHICULO', 'VEHICLE', 'MARCAMODELOYANODELAUTO'];
const YEAR_KEYS = ['ANODELAUTO', 'ANO', 'YEAR'];
const COLOR_KEYS = ['COLOR', 'COLOUR'];
const VIN_KEYS = ['VIN'];

export interface PrestigeField {
    label: string;
    value: string;
}

export interface PrestigeBillColumn {
    title: string;
    lines: PrestigeField[];
}

export interface PrestigeColumn {
    key: string;
    label: string;
    align: 'center' | 'right';
    flex: boolean;
    width: number;
}

export interface PrestigeLineRow {
    key: string;
    cells: string[];
}

export interface PrestigeTotalRow {
    key: string;
    label: string;
    value: string;
}

export interface PrestigeInvoiceModel {
    title: string;
    dateLabel: string;
    issuedOn: string;
    billToLabel: string;
    logoImageUrl: string;
    wordmark: string;
    sellerLines: string[];
    billToColumns: PrestigeBillColumn[];
    billToDetails: PrestigeField[];
    columns: PrestigeColumn[];
    showColumnSpacer: boolean;
    rows: PrestigeLineRow[];
    currencySymbol: string;
    summary: PrestigeTotalRow[];
    total: string;
    footerImages: { id: string; imageUrl: string; size?: Invoice['document']['design']['logo']['size'] }[];
    footerLines: string[];
    note: string;
    isVoid: boolean;
}

const markedTaxed = (value: string) => /^(true|yes|y|x|1|si)$/i.test(value.trim());

export const prestigeDate = (isoDate: string) => {
    const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    return `${match[3]}/${match[2]}/${match[1].slice(2)}`;
};

export const prestigeAmount = (
    amountMinor: number,
    currency: string,
    locale: string,
) => {
    if (!amountMinor) return '-';
    const formatted = formatMinor(Math.abs(amountMinor), currency, locale);
    const number = formatted.replace(/^[^\d]+/, '').trim();
    if (!number) return '-';
    return amountMinor < 0 ? `-${number}` : number;
};

const sellerLines = (invoice: Invoice) => {
    const { seller } = invoice.document.design;
    const address = seller.address || {};
    const contact = seller.contact || { email: '', phone: '', website: '' };
    const place = [address.region, address.postalCode]
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join(' ');
    const direccion = [address.line1, address.line2, address.city, place]
        .map((part) => (part || '').trim())
        .filter(Boolean)
        .join(' , ');
    const email = (contact.email || '').trim();
    const phone = (contact.phone || '').trim();
    const agent = invoice.createdBy?.displayName?.trim() || '';
    return [
        direccion ? `DIRECCION: ${direccion.toUpperCase()}` : '',
        email ? `CORREO: ${email.toUpperCase()}` : '',
        phone ? `TELEFONO: ${phone}` : '',
        agent ? `AGENTE: ${agent}` : '',
    ].filter(Boolean);
};

const currencySymbol = (currency: string, locale: string) => {
    const formatted = formatMinor(0, currency, locale);
    const symbol = formatted.match(/^[^\d-]+/)?.[0]?.trim();
    return symbol || '$';
};

const fieldEntry = (
    label: string,
    value: string,
): PrestigeField => ({
    label: label.trim().toUpperCase(),
    value: value.trim().toUpperCase(),
});

const customerFieldSet = (
    fields: Invoice['document']['design']['customerFields'],
) => new Set(fields?.includes('name') ? fields : []);

const billToValues = (invoice: Invoice) => {
    const values = new Map<string, string>();
    visibleBillToFields(invoice.document.design.billToFields).forEach((field) => {
        const key = normalizeLabel(field.label);
        if (values.has(key)) return;
        values.set(key, formatCustomValue(
            getBillToCustomValue(invoice.document.billToCustomValues, field.id),
            field.valueKind,
        ).trim().toUpperCase());
    });
    return values;
};

const takeBillValue = (values: Map<string, string>, keys: string[], used: Set<string>) => {
    keys.forEach((key) => used.add(key));
    const match = keys.map((key) => values.get(key) || '').find((value) => value);
    return match || '';
};

const partyColumn = (
    title: string,
    nombre: string,
    telefono: string,
    ci: string,
): PrestigeBillColumn | null => {
    const lines = [
        nombre ? fieldEntry('NOMBRE', nombre) : null,
        telefono ? fieldEntry('TELEFONO', telefono) : null,
        ci ? fieldEntry('CI', ci) : null,
    ].filter((line): line is PrestigeField => Boolean(line));
    if (!lines.length) return null;
    return { title, lines };
};

const billToSection = (invoice: Invoice) => {
    const { document: doc } = invoice;
    const values = billToValues(invoice);
    const used = new Set<string>();
    const customerFields = customerFieldSet(doc.design.customerFields);
    const remitenteNombre = takeBillValue(values, REMITENTE_NOMBRE, used)
        || (customerFields.has('name') ? doc.buyer.name : '');
    const remitenteTelefono = takeBillValue(values, REMITENTE_TELEFONO, used)
        || (customerFields.has('phone') ? doc.buyer.phone : '');
    const columns = [
        partyColumn(
            'REMITENTE',
            remitenteNombre,
            remitenteTelefono,
            takeBillValue(values, REMITENTE_CI, used),
        ),
        partyColumn(
            'DESTINATARIO',
            takeBillValue(values, DESTINATARIO_NOMBRE, used),
            takeBillValue(values, DESTINATARIO_TELEFONO, used),
            takeBillValue(values, DESTINATARIO_CI, used),
        ),
    ].filter((column): column is PrestigeBillColumn => Boolean(column));
    const details = [
        fieldEntry('AUTO', takeBillValue(values, AUTO_KEYS, used)),
        fieldEntry('AÑO', takeBillValue(values, YEAR_KEYS, used)),
        fieldEntry('COLOR', takeBillValue(values, COLOR_KEYS, used)),
        fieldEntry('VIN', takeBillValue(values, VIN_KEYS, used)),
    ].filter((line) => line.value);
    visibleBillToFields(doc.design.billToFields).forEach((field) => {
        const key = normalizeLabel(field.label);
        if (used.has(key)) return;
        used.add(key);
        const entry = fieldEntry(field.label, values.get(key) || '');
        if (entry.value) details.push(entry);
    });
    return { columns, details };
};

const columnModel = (column: InvoiceBuilderColumn): PrestigeColumn => {
    if (column.kind === 'description') {
        return {
            key: column.id,
            label: column.label.trim().toUpperCase() || 'DESCRIPTION',
            align: 'center',
            flex: true,
            width: 0,
        };
    }
    if (column.kind === 'amount') {
        return {
            key: column.id,
            label: column.label.trim().toUpperCase() || 'AMOUNT',
            align: 'right',
            flex: false,
            width: 72,
        };
    }
    return {
        key: column.id,
        label: column.label.trim().toUpperCase(),
        align: 'center',
        flex: false,
        width: column.kind === 'quantity' || column.kind === 'rate' ? 48 : 54,
    };
};

const lineCell = (
    column: InvoiceBuilderColumn,
    line: InvoiceLine,
    currency: string,
    locale: string,
) => {
    if (column.kind === 'description') return line.description;
    if (column.kind === 'quantity') return line.quantity;
    if (column.kind === 'rate' || column.kind === 'amount') {
        const minor = column.kind === 'rate' ? line.rateMinor : line.amounts.totalMinor;
        if (!minor) return '';
        return prestigeAmount(minor, currency, locale);
    }
    if (column.kind !== 'custom') return '';
    const raw = getCustomValue(line.customValues, column.id);
    if ((column as InvoiceBuilderCustomColumn).valueKind === 'boolean') {
        return markedTaxed(raw) ? 'X' : '';
    }
    return formatCustomValue(raw, column.valueKind);
};

export const getPrestigeInvoiceModel = (invoice: Invoice): PrestigeInvoiceModel => {
    const { document: doc } = invoice;
    const { design } = doc;
    const columns = visibleColumns(design.columns);
    const locale = design.regional.locale || 'en-US';
    const currency = doc.totals.currency || design.regional.currency || 'USD';
    const sourceColumns: InvoiceBuilderColumn[] = columns.length > 0 ? columns : [{
        id: 'description',
        kind: 'description',
        label: 'Description',
        isVisible: true,
        order: 0,
    }];
    const tableColumns = sourceColumns.map(columnModel);
    const dataRows = doc.lines.map((line) => ({
        key: line.id,
        cells: sourceColumns.map((column) => lineCell(column, line, currency, locale)),
    }));
    const rows = dataRows;
    while (rows.length < PRESTIGE_BODY_ROWS) {
        rows.push({
            key: `blank-${rows.length}`,
            cells: tableColumns.map(() => ''),
        });
    }

    const fees = doc.totals.fees || [];
    const summary: PrestigeTotalRow[] = [
        {
            key: 'subtotal',
            label: 'Subtotal',
            value: prestigeAmount(doc.totals.subtotalMinor, currency, locale),
        },
        ...fees.map((fee) => ({
            key: fee.id,
            label: fee.label,
            value: prestigeAmount(fee.amountMinor, currency, locale),
        })),
    ];

    const footerBlocks = [...(design.footerBlocks || [])]
        .sort((left, right) => left.order - right.order);
    const billTo = billToSection(invoice);

    return {
        title: (design.labels.documentTitle || 'FACTURA').trim().toUpperCase(),
        dateLabel: 'DATE',
        issuedOn: prestigeDate(invoice.issuedOn),
        billToLabel: (design.labels.billTo || 'BILL TO').trim().toUpperCase(),
        logoImageUrl: design.logo.style === 'image' ? design.logo.imageUrl : '',
        wordmark: design.logo.style === 'wordmark' ? design.logo.text : '',
        sellerLines: sellerLines(invoice),
        billToColumns: billTo.columns,
        billToDetails: billTo.details,
        columns: tableColumns,
        showColumnSpacer: tableColumns.filter((column) => !column.flex).length === 2,
        rows,
        currencySymbol: currencySymbol(currency, locale),
        summary,
        total: prestigeAmount(doc.totals.totalMinor, currency, locale),
        footerImages: footerBlocks.flatMap((block) => (
            block.kind === 'image' && block.imageUrl
                ? [{ id: block.id, imageUrl: block.imageUrl, size: block.size }]
                : []
        )),
        footerLines: footerBlocks.flatMap((block) => (
            block.kind === 'text' && block.markdown.trim() ? [block.markdown] : []
        )),
        note: invoiceNotes(invoice),
        isVoid: invoice.status === 'void',
    };
};
